import { motion } from "framer-motion";
import { MapPin, Navigation, Clock } from "lucide-react";
import { CONTACT, IMAGES } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

const MAPS_QUERY = encodeURIComponent(
  "MEGA HAIR - SALÃO ESTRELA, Quadra 8, Bloco 12, Loja 06, Sobradinho, Brasília - DF",
);
const MAPS_EMBED_URL = `https://maps.google.com/maps?q=${MAPS_QUERY}&z=16&output=embed`;
const MAPS_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${MAPS_QUERY}`;

export const NossoEspaco = () => (
  <section id="salao" data-testid="salon-section" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
    <div className="grid items-end gap-10 lg:grid-cols-2">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
      >
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-[#F9F8F6]">
          Nosso Espaço
        </p>
        <h2 className="font-display text-3xl font-semibold tracking-tight text-[#F9F8F6] sm:text-4xl lg:text-5xl">
          Venha nos <span className="italic text-gold-gradient">visitar</span>
        </h2>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-[#A1A09E] sm:text-lg">
          Um salão completo em Sobradinho, com estrutura confortável e uma equipe
          pronta para receber você.
        </p>
        <p className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-[#A1A09E]">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#F9F8F6]" />
          {CONTACT.address}
        </p>
        <p data-testid="salon-hours" className="mt-3 flex items-start gap-3 text-sm leading-relaxed text-[#A1A09E]">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#F9F8F6]" />
          Aberto todos os dias, de segunda a segunda, das 08h às 19h
        </p>
      </motion.div>
    </div>

    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:gap-8">
      <motion.figure
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="group relative overflow-hidden rounded-2xl border border-white/5 transition-colors duration-500 hover:border-[#D4AF37]/40"
      >
        <img
          src={IMAGES.fachada}
          alt="Fachada do Salão Estrela em Sobradinho"
          loading="lazy"
          data-testid="salon-photo-fachada"
          className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        />
        <figcaption className="absolute bottom-4 left-4 rounded-full border border-[#D4AF37]/40 bg-[#0A0A0B]/70 px-4 py-1.5 text-[9px] font-bold uppercase tracking-[0.25em] text-[#F9F8F6] backdrop-blur-md">
          Nossa fachada
        </figcaption>
      </motion.figure>
      <motion.figure
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.12 }}
        className="group relative overflow-hidden rounded-2xl border border-white/5 transition-colors duration-500 hover:border-[#D4AF37]/40"
      >
        <img
          src={IMAGES.salao}
          alt="Interior do Salão Estrela com equipe atendendo clientes"
          loading="lazy"
          data-testid="salon-photo-interior"
          className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        />
        <figcaption className="absolute bottom-4 left-4 rounded-full border border-[#D4AF37]/40 bg-[#0A0A0B]/70 px-4 py-1.5 text-[9px] font-bold uppercase tracking-[0.25em] text-[#F9F8F6] backdrop-blur-md">
          Por dentro do salão
        </figcaption>
      </motion.figure>
    </div>

    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
      className="mt-6 lg:mt-8"
    >
      <div className="relative overflow-hidden rounded-2xl border border-white/5 transition-colors duration-500 hover:border-[#D4AF37]/40">
        <iframe
          src={MAPS_EMBED_URL}
          title="Mapa do Salão Estrela — Quadra 8, Bloco 12, Loja 06, Sobradinho, Brasília/DF"
          data-testid="salon-map-iframe"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
          className="h-72 w-full grayscale-[35%] sm:h-96"
        />
      </div>
      <div className="mt-6 text-center">
        <a
          href={MAPS_DIRECTIONS_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="salon-directions-btn"
          className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#F9F8F6] transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#0A0A0B] hover:text-[#F9F8F6]"
        >
          <Navigation className="h-4 w-4" />
          Traçar rota até o salão
        </a>
      </div>
    </motion.div>
  </section>
);
